import { defineStore } from 'pinia';
import { ref } from 'vue';
import { topupApi, type DepositInfo, type RecentTopup } from '../api/topup';

export interface PendingTx {
  txHash: string;
  expectedAmountNgonka: string;
  broadcastedAt: number;
}

export const useTopupStore = defineStore('topup', () => {
  const depositInfo = ref<DepositInfo | null>(null);
  const recent = ref<RecentTopup[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  // Optional: the most recent one-click tx the user broadcast through Keeper.
  // Surfaced for UX hints ("waiting for confirmation") — NOT a source of
  // truth; the watcher credits balance independently.
  const lastPendingTx = ref<PendingTx | null>(null);

  async function loadDepositInfo(force = false): Promise<void> {
    if (depositInfo.value && !force) return;
    try {
      depositInfo.value = await topupApi.getDepositInfo();
    } catch (err) {
      error.value = (err as Error).message;
    }
  }

  async function loadRecent(): Promise<void> {
    try {
      recent.value = await topupApi.getRecent(20);
    } catch (err) {
      error.value = (err as Error).message;
    }
  }

  async function refresh(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await loadRecent();
    } finally {
      loading.value = false;
    }
  }

  async function loadAll(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await Promise.all([loadDepositInfo(), loadRecent()]);
    } finally {
      loading.value = false;
    }
  }

  function setPendingTx(tx: PendingTx | null): void {
    lastPendingTx.value = tx;
  }

  return {
    depositInfo,
    recent,
    loading,
    error,
    lastPendingTx,
    loadDepositInfo,
    loadRecent,
    refresh,
    loadAll,
    setPendingTx,
  };
});
